import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  createBook, deleteBook, issueBook, KIND_LABELS,
  listBooks, listCheckouts, myCheckouts, returnBook, updateBook,
} from "service/LibraryService";
import { listStudents } from "service/StudentsService";
import Notification from "utils/Notifications";
import { PageHeader, UnvButton, UnvInput, UnvModal, Card, Icon, EmptyState } from "components";
import s from "./LibraryPage.module.scss";

export default function LibraryPage() {
  const { t } = useTranslation('common');
  const auth = useSelector((st) => st.auth);
  const codes = (auth?.roles || []).map((r) => (typeof r === "string" ? r : r?.code));
  const isStaff = codes.some((c) => ["archivist", "rectorate", "sysadmin", "methodist"].includes(c));
  const isStudent = codes.includes("student");

  const [tab, setTab] = useState(isStaff ? "catalog" : "my");
  const [books, setBooks] = useState([]);
  const [checkouts, setCheckouts] = useState([]);
  const [my, setMy] = useState([]);
  const [students, setStudents] = useState([]);
  const [editing, setEditing] = useState(null);
  const [issuing, setIssuing] = useState(null);
  const [filter, setFilter] = useState({ q: "", kind: "", available: false });
  const [notify, setNotify] = useState({ isOpen: false });

  const reloadBooks = () => {
    const params = {};
    if (filter.q) params.q = filter.q;
    if (filter.kind) params.kind = filter.kind;
    if (filter.available) params.available = "1";
    listBooks(params).then((r) => setBooks(r.data?.results || r.data || []));
  };
  const reloadCheckouts = () => listCheckouts().then((r) => setCheckouts(r.data?.results || r.data || []));
  const reloadMy = () => myCheckouts().then((r) => setMy(r.data?.results || r.data || []));

  useEffect(() => {
    reloadBooks();
    if (isStaff) {
      reloadCheckouts();
      listStudents({ page_size: 200 }).then((r) => setStudents(r.data?.results || r.data || []));
    } else {
      reloadMy();
    }
    // eslint-disable-next-line
  }, [filter.kind, filter.available]);

  const saveBook = async () => {
    try {
      if (editing.id) await updateBook(editing.id, editing);
      else await createBook(editing);
      setNotify({ isOpen: true, message: t('library.saved'), type: "success" });
      setEditing(null);
      reloadBooks();
    } catch (e) { setNotify({ isOpen: true, message: e.response?.data?.detail || t('common.error'), type: "error" }); }
  };

  const issue = async () => {
    try {
      await issueBook({
        book: issuing.book, student: issuing.student, days: issuing.days || 14,
      });
      setNotify({ isOpen: true, message: t('library.issued'), type: "success" });
      setIssuing(null);
      reloadBooks();
      reloadCheckouts();
    } catch (e) { setNotify({ isOpen: true, message: e.response?.data?.detail || t('common.error'), type: "error" }); }
  };

  const ret = async (id) => {
    if (!window.confirm(t('library.confirm_return'))) return;
    await returnBook(id);
    reloadCheckouts(); reloadBooks();
  };

  return (
    <div className={s.page}>
      <div className={s.toolbar}>
        <h2 className={s.title}>{t('library.title_page')}</h2>
        <div className={s.tabs} role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'catalog'}
            className={`${s.tab} ${tab === 'catalog' ? s.active : ''}`}
            onClick={() => setTab('catalog')}
          >
            {t('library.tab_catalog')}
          </button>
          {isStaff && (
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'checkouts'}
              className={`${s.tab} ${tab === 'checkouts' ? s.active : ''}`}
              onClick={() => setTab('checkouts')}
            >
              {t('library.tab_checkouts')}
            </button>
          )}
          {!isStaff && (
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'my'}
              className={`${s.tab} ${tab === 'my' ? s.active : ''}`}
              onClick={() => setTab('my')}
            >
              {t('library.tab_my')}
            </button>
          )}
        </div>
      </div>

      {tab === "catalog" && (
        <>
          <div className={s.toolbar}>
            <input type="text" className="unv-input" placeholder={t('library.search_placeholder')}
                   value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })}
                   onKeyDown={(e) => e.key === "Enter" && reloadBooks()} />
            <select className="unv-input" value={filter.kind}
                    onChange={(e) => setFilter({ ...filter, kind: e.target.value })}>
              <option value="">{t('library.all_kinds')}</option>
              {Object.entries(KIND_LABELS).map(([k, v]) => (<option key={k} value={k}>{v}</option>))}
            </select>
            <label><input type="checkbox" checked={filter.available}
                          onChange={(e) => setFilter({ ...filter, available: e.target.checked })} /> {t('library.available_only')}</label>
            <button className="unv-btn" onClick={reloadBooks}>{t('library.find_btn')}</button>
            {isStaff && <button className="unv-btn unv-btn--primary"
                                onClick={() => setEditing({ kind: "textbook", language: "ru", copies_total: 1, copies_available: 1 })}>
              {t('library.add_book')}
            </button>}
          </div>
          <table className={s.table}>
            <thead><tr><th>{t('library.col_title')}</th><th>{t('library.col_author')}</th><th>{t('library.col_year')}</th><th>{t('library.col_kind')}</th><th>{t('library.col_in_stock')}</th><th></th></tr></thead>
            <tbody>
              {books.map((b) => (
                <tr key={b.id}>
                  <td><b>{b.title}</b></td>
                  <td>{b.authors || "—"}</td>
                  <td>{b.year || "—"}</td>
                  <td>{KIND_LABELS[b.kind]}</td>
                  <td>{b.copies_available} / {b.copies_total}</td>
                  <td>
                    {isStaff && (
                      <>
                        <button className={s.smallBtn} onClick={() => setEditing(b)}>{t('library.edit_short')}</button>
                        <button className={s.smallBtn} onClick={() => setIssuing({ book: b.id, days: 14 })} disabled={b.copies_available <= 0}>{t('library.issue')}</button>
                        <button className={s.smallBtn} onClick={async () => {
                          if (window.confirm(t('library.delete_confirm'))) { await deleteBook(b.id); reloadBooks(); }
                        }}>{t('common.delete')}</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {tab === "checkouts" && isStaff && (
        <table className={s.table}>
          <thead><tr><th>{t('library.col_book')}</th><th>{t('library.col_to_whom')}</th><th>{t('library.col_issued')}</th><th>{t('library.col_due')}</th><th>{t('library.col_returned')}</th><th>{t('library.col_status')}</th><th></th></tr></thead>
          <tbody>
            {checkouts.map((c) => (
              <tr key={c.id}>
                <td>{c.book_title}</td><td>{c.borrower_label}</td>
                <td>{c.checked_out_at}</td><td>{c.due_at}</td>
                <td>{c.returned_at || "—"}</td><td>{c.status_display}</td>
                <td>{c.status === "active" || c.status === "overdue"
                  ? <button className={s.smallBtn} onClick={() => ret(c.id)}>{t('library.accept_return')}</button>
                  : null}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {tab === "my" && !isStaff && (
        <table className={s.table}>
          <thead><tr><th>{t('library.col_book')}</th><th>{t('library.col_issued')}</th><th>{t('library.col_due')}</th><th>{t('library.col_status')}</th></tr></thead>
          <tbody>
            {my.map((c) => (
              <tr key={c.id}>
                <td>{c.book_title}</td><td>{c.checked_out_at}</td>
                <td>{c.due_at}</td><td>{c.status_display}</td>
              </tr>
            ))}
            {my.length === 0 && <tr><td colSpan={4} style={{ textAlign: "center", color: "#9aa4bc" }}>{t('library.no_active_checkouts')}</td></tr>}
          </tbody>
        </table>
      )}

      {editing && (
        <div className={s.overlay} onClick={() => setEditing(null)}>
          <div className={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>{editing.id ? t('library.modal_edit_title') : t('library.modal_new_book')}</h3>
            <div className={s.field}><label>{t('library.field_title')}</label>
              <input className="unv-input" value={editing.title || ""}
                     onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></div>
            <div className={s.field}><label>{t('library.field_authors')}</label>
              <input className="unv-input" value={editing.authors || ""}
                     onChange={(e) => setEditing({ ...editing, authors: e.target.value })} /></div>
            <div className={s.field}><label>{t('library.field_publisher_year')}</label>
              <input className="unv-input" value={editing.publisher || ""}
                     onChange={(e) => setEditing({ ...editing, publisher: e.target.value })}
                     style={{ marginBottom: 6 }} />
              <input type="number" className="unv-input" placeholder={t('library.field_year_placeholder')}
                     value={editing.year || ""}
                     onChange={(e) => setEditing({ ...editing, year: Number(e.target.value) })} /></div>
            <div className={s.field}><label>{t('library.field_isbn')}</label>
              <input className="unv-input" value={editing.isbn || ""}
                     onChange={(e) => setEditing({ ...editing, isbn: e.target.value })} /></div>
            <div className={s.field}><label>{t('library.field_kind')}</label>
              <select className="unv-input" value={editing.kind || "textbook"}
                      onChange={(e) => setEditing({ ...editing, kind: e.target.value })}>
                {Object.entries(KIND_LABELS).map(([k, v]) => (<option key={k} value={k}>{v}</option>))}
              </select></div>
            <div className={s.field}>
              <label>{t('library.field_copies')}</label>
              <input type="number" className="unv-input" value={editing.copies_total || 1}
                     onChange={(e) => setEditing({ ...editing, copies_total: Number(e.target.value) })}
                     style={{ marginBottom: 6 }} />
              <input type="number" className="unv-input" value={editing.copies_available || 0}
                     onChange={(e) => setEditing({ ...editing, copies_available: Number(e.target.value) })} />
            </div>
            <div className={s.field}><label>{t('library.field_annotation')}</label>
              <textarea className="unv-input" rows={3} value={editing.annotation || ""}
                        onChange={(e) => setEditing({ ...editing, annotation: e.target.value })} /></div>
            <div className={s.actions}>
              <button className="unv-btn" onClick={() => setEditing(null)}>{t('common.cancel')}</button>
              <button className="unv-btn unv-btn--primary" onClick={saveBook}>{t('common.save')}</button>
            </div>
          </div>
        </div>
      )}

      {issuing && (
        <div className={s.overlay} onClick={() => setIssuing(null)}>
          <div className={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>{t('library.issue_book_title')}</h3>
            <div className={s.field}><label>{t('library.field_student')}</label>
              <select className="unv-input" value={issuing.student || ""}
                      onChange={(e) => setIssuing({ ...issuing, student: Number(e.target.value) })}>
                <option value="">—</option>
                {students.map((st) => (<option key={st.id} value={st.id}>{st.full_name || `${st.surname} ${st.first_name}`}</option>))}
              </select></div>
            <div className={s.field}><label>{t('library.field_days')}</label>
              <input type="number" className="unv-input" value={issuing.days}
                     onChange={(e) => setIssuing({ ...issuing, days: Number(e.target.value) })} /></div>
            <div className={s.actions}>
              <button className="unv-btn" onClick={() => setIssuing(null)}>{t('common.cancel')}</button>
              <button className="unv-btn unv-btn--primary" onClick={issue}>{t('library.issue')}</button>
            </div>
          </div>
        </div>
      )}

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  );
}
